// Player identity. The bearer token is captured from the OAuth redirect hash
// (#auth=<token>) or app-password login, persisted to localStorage, and used
// for authed game calls. isAdmin is server-decided (ADMIN_EMAIL).
import { create } from 'zustand'
import { getMe, blueskyLogin, logout as apiLogout } from '../api/gg'
import { useBsky } from './bsky'
import type { MeResponse, SessionUser } from '../types'

const TOKEN_KEY = 'gg_token'

interface AuthState {
  token: string | null
  user: SessionUser | null
  isAdmin: boolean
  providers: MeResponse['providers']
  ready: boolean
  authError: string | null
  init: () => Promise<void>
  refreshMe: () => Promise<void>
  loginBluesky: (service: string, identifier: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

function readHashToken(): { token?: string; error?: string } {
  const h = window.location.hash
  if (!h) return {}
  const m = h.match(/[#&]auth=([^&]+)/)
  if (m) return { token: decodeURIComponent(m[1]) }
  const e = h.match(/[#&]auth_error=([^&]+)/)
  if (e) return { error: decodeURIComponent(e[1]) }
  return {}
}

export const useAuth = create<AuthState>((set, get) => ({
  token: null,
  user: null,
  isAdmin: false,
  providers: { bluesky: true, google: false, yandex: false, apple: false },
  ready: false,
  authError: null,

  async init() {
    const { token, error } = readHashToken()
    if (token || error) {
      // Strip the auth fragment so a refresh doesn't re-process it.
      history.replaceState(null, '', window.location.pathname + window.location.search)
    }
    if (error) set({ authError: error })
    const stored = token ?? localStorage.getItem(TOKEN_KEY)
    if (token) localStorage.setItem(TOKEN_KEY, token)
    set({ token: stored ?? null })
    await get().refreshMe()
    set({ ready: true })
  },

  async refreshMe() {
    const token = get().token
    try {
      const me = await getMe(token)
      set({ user: me.user, isAdmin: me.isAdmin, providers: me.providers })
      // A stale/invalid token returns user:null — drop it.
      if (token && !me.user) {
        localStorage.removeItem(TOKEN_KEY)
        set({ token: null })
      }
    } catch {
      set({ user: null, isAdmin: false })
    }
  },

  async loginBluesky(service, identifier, password) {
    const res = await blueskyLogin(service, identifier, password)
    localStorage.setItem(TOKEN_KEY, res.token)
    // Keep the Bluesky session for authed content fetching.
    useBsky.getState().setSession(res.session)
    set({ token: res.token, authError: null })
    await get().refreshMe()
  },

  async logout() {
    const token = get().token
    if (token) await apiLogout(token)
    localStorage.removeItem(TOKEN_KEY)
    useBsky.getState().clear()
    set({ token: null, user: null, isAdmin: false })
  },
}))
